import { CurrencyService } from './services/CurrencyService';
import type { Init, CurrencyUrlAdapter } from './types';
import { managerHelpers } from '../../helpers';
import type { MergedDataReturnType, MergedHandlersReturnType } from '../../types';
import type { WidgetLifecycle } from '../WidgetLifecycle';
import { WithApi } from '../WithApi';

export type CurrencyData = MergedDataReturnType<[CurrencyService]>;

export type CurrencyHandlers = MergedHandlersReturnType<[CurrencyService]>;

type TApi = ReturnType<CurrencyService['api']>;

export class CurrencyManager extends WithApi<TApi> {
  constructor(private urlAdapter: CurrencyUrlAdapter) {
    super();
  }

  public currencyInit = ({ pageStore, queries, mutations, flags }: Init) => {
    const [availableCurrenciesQuery, billingAccountQuery] = queries;

    const [updateCurrencyMutation] = mutations;

    const pending = availableCurrenciesQuery.isPending || billingAccountQuery.isPending;

    const error = availableCurrenciesQuery.error || billingAccountQuery.error;

    if (pending) {
      return {
        status: 'pending',
        data: null,
        handlers: null,
        lifecycle: {} as WidgetLifecycle,
      } as const;
    }

    if (error) {
      return {
        status: 'error',
        data: error,
        handlers: {
          retry: () => {
            managerHelpers.retryQueries(queries);
          },
        },
        lifecycle: {} as WidgetLifecycle,
      } as const;
    }

    const currency = new CurrencyService(
      pageStore,
      availableCurrenciesQuery.data,
      billingAccountQuery.data,
      updateCurrencyMutation,
      this.urlAdapter,
      flags
    );

    this.initApi({
      ...currency.api(),
    });

    return {
      status: 'loaded',
      data: {
        ...currency.data(),
      },
      handlers: {
        ...currency.handlers(),
      },
      lifecycle: {
        onMount() {
          currency.onMount();
        },
      } as WidgetLifecycle,
    } as const;
  };
}
